import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { HiOutlineUserGroup } from 'react-icons/hi2';

import { LEGAL } from '../data/legalContent';
import { COMPARE } from '../data/productCopy';
import { trackEvent } from '../utils/analytics';

const Card = styled(motion.section)`
  width: 100%;
  margin-bottom: 1.5rem;
  padding: 1.5rem 1.35rem;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(18, 18, 28, 0.65) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--type-accent, var(--primary-color));
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--type-accent, var(--primary-color));
  margin-bottom: 0.5rem;
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  margin: 0 0 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 0.98rem;
  max-width: 640px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const PrimaryButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--type-accent, var(--primary-color));
  color: #0c0f14;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.8rem 1.15rem;
  border-radius: 12px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgba(var(--primary-rgb), 0.65);
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: var(--text-primary);
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.8rem 1.15rem;
  border-radius: 12px;
  text-decoration: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgba(var(--primary-rgb), 0.65);
    outline-offset: 2px;
  }
`;

const Status = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.88rem;
  color: var(--type-accent, var(--primary-color));
`;

const SoonBadge = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.75);
  vertical-align: middle;
`;

const CofounderCompareTeaser = ({ shareUrl, variants }) => {
  const [copied, setCopied] = useState(false);

  const inviteText = shareUrl
    ? `I'm mapping my founder operating profile on FounderDNA. Take the 3-minute assessment and we can compare how we lead: ${shareUrl}`
    : '';

  const handleCopy = async () => {
    if (!inviteText || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(inviteText);
      setCopied(true);
      trackEvent('cofounder_invite_copied');
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      // Non blocking
    }
  };

  const waitlistHref = `mailto:${LEGAL.contactEmail}?subject=${encodeURIComponent(COMPARE.waitlistSubject)}&body=${encodeURIComponent(COMPARE.waitlistBody)}`;

  return (
    <Card
      as={variants ? motion.section : 'section'}
      variants={variants}
      aria-labelledby="cofounder-compare-heading"
    >
      <Eyebrow>
        <HiOutlineUserGroup aria-hidden="true" />
        Co-founder fit
        <SoonBadge>{COMPARE.comingSoon}</SoonBadge>
      </Eyebrow>
      <Title id="cofounder-compare-heading">{COMPARE.title}</Title>
      <Description>{COMPARE.description}</Description>
      <Actions>
        {shareUrl && (
          <PrimaryButton type="button" onClick={handleCopy} whileTap={{ scale: 0.98 }}>
            {COMPARE.copyLinkCta}
          </PrimaryButton>
        )}
        <SecondaryButton
          href={waitlistHref}
          onClick={() => trackEvent('compare_waitlist_clicked')}
        >
          {COMPARE.waitlistCta}
        </SecondaryButton>
      </Actions>
      {copied && <Status role="status">Invite copied. Send it to your co-founder.</Status>}
    </Card>
  );
};

export default CofounderCompareTeaser;
